import { Typography, makeStyles, Theme, Container, Box, Button } from '@material-ui/core';
import { primaryGreen, darkGreen } from 'src/data/colors';
import { useHistory } from 'react-router-dom';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(4),
    maxWidth: '1200px',
    margin: '0 auto',
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: primaryGreen,
  },
  subtitle: {
    fontSize: 20,
    fontWeight: 500,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
  paragraph: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: 16,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  listItem: {
    display: 'flex',
    marginBottom: theme.spacing(1),
  },
  bullet: {
    marginRight: theme.spacing(1),
  },
  bold: {
    fontWeight: 700,
  },
  link: {
    color: primaryGreen,
    '&:hover': {
      color: darkGreen,
    },
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    color: primaryGreen,
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    marginLeft: -theme.spacing(1),
    '&:hover': {
      color: darkGreen,
      background: 'transparent',
      textDecoration: 'none',
    },
  },
}));

const PrivacyPolicy = () => {
  const classes = useStyles();
  const history = useHistory();

  const handleGoBack = () => {
    history.goBack();
  };

  return (
    <Container className={classes.root}>
      <Button
        className={classes.backButton}
        onClick={handleGoBack}
        startIcon={<ArrowBackIcon fontSize="small" />}
        disableRipple
      >
        Go Back
      </Button>

      <Typography variant="h1" className={classes.title}>
        Fortress Keystone Web Portal Privacy Policy
      </Typography>

      <Typography variant="body1" className={classes.paragraph}>
        Fortress Power respects your privacy and is committed to protecting the personal information
        you share with us through the Fortress Keystone Web Portal. This policy explains what data
        we collect, why we collect it, and what choices you have.
      </Typography>

      <Typography variant="h2" className={classes.subtitle}>
        1. Information We Collect
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        When you use the Fortress Keystone Web Portal, we collect:
      </Typography>

      <Typography variant="h3" className={classes.sectionTitle}>
        a. Personal Information
      </Typography>
      <Box pl={2}>
        <Box className={classes.listItem}>
          <Typography variant="body1" className={classes.bullet}>
            •
          </Typography>
          <Typography variant="body1">Your name</Typography>
        </Box>
        <Box className={classes.listItem}>
          <Typography variant="body1" className={classes.bullet}>
            •
          </Typography>
          <Typography variant="body1">Email address</Typography>
        </Box>
        <Box className={classes.listItem}>
          <Typography variant="body1" className={classes.bullet}>
            •
          </Typography>
          <Typography variant="body1">Company or installer affiliation</Typography>
        </Box>
        <Box className={classes.listItem}>
          <Typography variant="body1" className={classes.bullet}>
            •
          </Typography>
          <Typography variant="body1">Phone number (if provided)</Typography>
        </Box>
      </Box>

      <Typography variant="h3" className={classes.sectionTitle}>
        b. Account and Usage Information
      </Typography>
      <Box pl={2}>
        <Box className={classes.listItem}>
          <Typography variant="body1" className={classes.bullet}>
            •
          </Typography>
          <Typography variant="body1">Login history and access logs</Typography>
        </Box>
        <Box className={classes.listItem}>
          <Typography variant="body1" className={classes.bullet}>
            •
          </Typography>
          <Typography variant="body1">
            Actions you take in the portal (e.g., viewing a site, updating settings)
          </Typography>
        </Box>
      </Box>

      <Typography variant="h3" className={classes.sectionTitle}>
        c. System and Site Data
      </Typography>
      <Box pl={2}>
        <Box className={classes.listItem}>
          <Typography variant="body1" className={classes.bullet}>
            •
          </Typography>
          <Typography variant="body1">Site names, addresses, and serial numbers</Typography>
        </Box>
        <Box className={classes.listItem}>
          <Typography variant="body1" className={classes.bullet}>
            •
          </Typography>
          <Typography variant="body1">Equipment status, alarms, and performance data</Typography>
        </Box>
        <Box className={classes.listItem}>
          <Typography variant="body1" className={classes.bullet}>
            •
          </Typography>
          <Typography variant="body1">Firmware version and device connectivity</Typography>
        </Box>
      </Box>

      <Typography variant="body1" className={classes.paragraph}>
        We do <span className={classes.bold}>not</span> collect or store sensitive personal
        identifiers like Social Security numbers, payment information, or homeownership documents.
      </Typography>

      <Typography variant="h2" className={classes.subtitle}>
        2. How We Use Your Information
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        We use your information to:
      </Typography>
      <Box pl={2}>
        <Box className={classes.listItem}>
          <Typography variant="body1" className={classes.bullet}>
            •
          </Typography>
          <Typography variant="body1">
            Operate and maintain the Fortress Keystone Web Portal
          </Typography>
        </Box>
        <Box className={classes.listItem}>
          <Typography variant="body1" className={classes.bullet}>
            •
          </Typography>
          <Typography variant="body1">
            Help support teams troubleshoot and monitor energy systems
          </Typography>
        </Box>
        <Box className={classes.listItem}>
          <Typography variant="body1" className={classes.bullet}>
            •
          </Typography>
          <Typography variant="body1">Send system alerts or important updates</Typography>
        </Box>
        <Box className={classes.listItem}>
          <Typography variant="body1" className={classes.bullet}>
            •
          </Typography>
          <Typography variant="body1">Improve product features and performance</Typography>
        </Box>
      </Box>

      <Typography variant="body1" className={classes.paragraph}>
        We do <span className={classes.bold}>not</span> sell or share your personal information for
        advertising purposes.
      </Typography>

      <Typography variant="h2" className={classes.subtitle}>
        3. Who Has Access
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        Only authorized users and Fortress Power employees with appropriate roles (such as system
        admins or support staff) can access site data and user accounts. Installers only have access
        to the sites assigned to their company.
      </Typography>

      <Typography variant="h2" className={classes.subtitle}>
        4. Your Privacy Rights (California Residents)
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        If you live in California, you have the right to:
      </Typography>
      <Box pl={2}>
        <Box className={classes.listItem}>
          <Typography variant="body1" className={classes.bullet}>
            •
          </Typography>
          <Typography variant="body1">
            Request a copy of the personal data we have about you
          </Typography>
        </Box>
        <Box className={classes.listItem}>
          <Typography variant="body1" className={classes.bullet}>
            •
          </Typography>
          <Typography variant="body1">Ask us to delete your data</Typography>
        </Box>
        <Box className={classes.listItem}>
          <Typography variant="body1" className={classes.bullet}>
            •
          </Typography>
          <Typography variant="body1">Request corrections to inaccurate information</Typography>
        </Box>
        <Box className={classes.listItem}>
          <Typography variant="body1" className={classes.bullet}>
            •
          </Typography>
          <Typography variant="body1">
            Opt out of the sale or sharing of your data (we do not sell or share personal data)
          </Typography>
        </Box>
      </Box>

      <Typography variant="body1" className={classes.paragraph}>
        To make a request, you can contact us at{' '}
        <a
          href="https://support.fortresspower.com/portal/en/newticket"
          target="_blank"
          className={classes.link}
          rel="noreferrer"
        >
          https://support.fortresspower.com/portal/en/newticket
        </a>
        , or use the account deletion feature in the mobile app.
      </Typography>

      <Typography variant="h2" className={classes.subtitle}>
        5. How to Delete Your Account
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        You can delete your account in one of two ways:
      </Typography>
      <Box pl={2}>
        <Box className={classes.listItem}>
          <Typography variant="body1" className={classes.bullet}>
            •
          </Typography>
          <Typography variant="body1">
            Use the "Delete Account" feature in the Fortress mobile app
          </Typography>
        </Box>
        <Box className={classes.listItem}>
          <Typography variant="body1" className={classes.bullet}>
            •
          </Typography>
          <Typography variant="body1">
            Submit a support ticket to{' '}
            <a
              href="https://support.fortresspower.com/portal/en/newticket"
              target="_blank"
              className={classes.link}
              rel="noreferrer"
            >
              https://support.fortresspower.com/portal/en/newticket
            </a>
          </Typography>
        </Box>
      </Box>
      <Typography variant="body1" className={classes.paragraph}>
        After deletion, we may retain limited data for system audit purposes or regulatory
        compliance.
      </Typography>

      <Typography variant="h2" className={classes.subtitle}>
        6. Data Security
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        We take reasonable steps to protect your information, including encryption of sensitive
        data, access controls, and audit logs. However, no system is 100% secure, and we encourage
        users to keep passwords private and use two-factor authentication if enabled.
      </Typography>

      <Typography variant="h2" className={classes.subtitle}>
        7. Updates to This Policy
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        We may update this policy as needed. If we make changes, we'll update the "Effective Date"
        at the top of the page and notify users if the changes are significant.
      </Typography>

      <Typography variant="h2" className={classes.subtitle}>
        8. Contact
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        If you have any questions about this privacy policy or how your data is handled, please
        contact:
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        Fortress Power
        <br />
        Website:{' '}
        <a
          href="https://www.fortresspower.com"
          target="_blank"
          className={classes.link}
          rel="noreferrer"
        >
          https://www.fortresspower.com
        </a>
      </Typography>
    </Container>
  );
};

export default PrivacyPolicy;
